/**
 * Voucher Builder — the unified, primary voucher creation experience.
 *
 * ONE PLACE for content + design + preview + save/publish. Entered from
 * Voucher Types → New / Edit. There is no separate design step and no
 * separate template flow needed to ship a voucher.
 *
 * 3-panel layout:
 *   LEFT   · Voucher content (code / name / subtitle / benefit /
 *            validity / terms / category / active). Real business
 *            fields backed by the existing voucher-type schema.
 *   CENTER · The canonical <VoucherDesignEditor> canvas, driven by
 *            the LIVE content typed on the left. No sample text is
 *            shown while a real voucher is being edited.
 *   RIGHT  · Property panel (handled inside the editor; canvas
 *            settings when nothing is selected).
 *
 * Top-bar actions:
 *   • Save Voucher              → persists content + design_template_id
 *   • Save Design as Template   → mints a reusable master from the
 *                                  current canvas + layers
 *   • Apply Existing Template   → binds a saved master to this voucher
 *                                  (business content stays untouched)
 *   • Preview: Desktop ↔ Mobile
 *
 * NO new server architecture is introduced. Reuses:
 *   - /admin/loyalty/voucher-types
 *   - /admin/loyalty/design-templates
 *   - VoucherDesignRenderer  (single canonical renderer for admin +
 *                              guest wallet)
 *   - VoucherDesignEditor    (canvas + layers + toolbar + presets)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft, Save, PenTool, Monitor, Smartphone, LibraryBig,
  BookmarkPlus, CheckCircle2, AlertTriangle, Layers,
} from 'lucide-react';
import VoucherDesignEditor from '../components/loyalty/VoucherDesignEditor';
import VoucherDesignRenderer from '../components/loyalty/VoucherDesignRenderer';
import VoucherPresentation from '../components/loyalty/VoucherPresentation';
import { COMPOSER_PRESETS, BLANK_PRESET } from '../components/loyalty/voucherComposerPresets';

const BENEFIT_TYPES = [
  { value: 'flat_discount',       label: 'Flat Discount (₹)' },
  { value: 'percent_discount',    label: 'Percentage Discount (%)' },
  { value: 'complimentary_item',  label: 'Complimentary Item' },
  { value: 'buy_one_get_one',     label: 'Buy One Get One' },
  { value: 'cashback',            label: 'Cashback' },
  { value: 'custom',              label: 'Custom' },
];

// Legacy vouchers surface a boolean design.design_version. We ONLY care
// about the shape the editor expects: { canvas, elements }.
// Phase 2A · new vouchers default to Start Blank (Premium Wide, no
// forced content layers). Legacy V1 designs continue to migrate.
const seedEditorDesign = (voucher) => {
  const d = voucher?.design;
  if (d && d.design_version >= 2 && Array.isArray(d.elements)) {
    return { design_version: 2, canvas: d.canvas || {}, elements: d.elements || [] };
  }
  return BLANK_PRESET.build();
};

const shortDate = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + Number(days || 30));
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' });
};

export default function AdminVoucherBuilder() {
  const { getHeaders, API } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();                            // /voucher-builder/:id?
  const [sp] = useSearchParams();
  // Accept both `outlet_id` (canonical) and legacy `outlet` search
  // params so pre-refactor bookmarks keep working.
  const outletFromQuery = sp.get('outlet_id') || sp.get('outlet') || '';
  const originFrom      = sp.get('from') || '';

  // Deterministic Back / return path. NEVER navigate(-1): if the user
  // opens the Builder directly (refresh, deep link), history is empty
  // and the browser would fall back to a blank page.
  const returnHref = useMemo(() => {
    const nsp = new URLSearchParams();
    // Default return is the Voucher Types tab when we came from there,
    // otherwise the Loyalty root (which then falls back to Dashboard).
    if (originFrom === 'vouchers' || originFrom === 'voucher-types') nsp.set('tab', 'vouchers');
    if (outletFromQuery) nsp.set('outlet_id', outletFromQuery);
    return `/admin/loyalty${nsp.toString() ? `?${nsp.toString()}` : ''}`;
  }, [originFrom, outletFromQuery]);

  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [previewMode, setPreviewMode] = useState('desktop');   // desktop | mobile

  // Business content ── mirrors the VoucherTypeEditor schema.
  const [content, setContent] = useState({
    outlet_id: outletFromQuery,
    code: '',
    name: '',
    description: '',
    benefit_type: 'flat_discount',
    benefit_value: '',
    validity_days: 30,
    active: true,
    is_giftable: false,
    // Phase 3B · Sales Catalogue toggle. Default OFF so Admin has
    // to explicitly authorise a voucher for Sales-team distribution.
    sales_enabled: false,
    terms: '',                    // stored inside description if the
                                  // canonical schema doesn't have it.
    design_template_id: '',
  });
  const [design, setDesign] = useState(BLANK_PRESET.build());
  const [templateName, setTemplateName] = useState('');       // for Save-as-template
  const [saveAsTplOpen, setSaveAsTplOpen] = useState(false);
  const [applyTplOpen, setApplyTplOpen]   = useState(false);
  const [bulkApplyOpen, setBulkApplyOpen] = useState(false);  // Phase 3B · Slice 4

  // ── Fetch context ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [ou, tp] = await Promise.all([
          axios.get(`${API}/outlets`, { headers: getHeaders() }),
          axios.get(`${API}/admin/loyalty/design-templates?status=draft`, { headers: getHeaders() })
            .catch(() => ({ data: { templates: [] } })),
        ]);
        setOutlets(Array.isArray(ou.data) ? ou.data : (ou.data?.outlets || []));
        const alsoPublished = await axios.get(`${API}/admin/loyalty/design-templates?status=published`,
                                              { headers: getHeaders() })
                                        .catch(() => ({ data: { templates: [] } }));
        setTemplates([...(tp.data?.templates || []), ...(alsoPublished.data?.templates || [])]);
      } catch (e) { /* soft-fail — the page still works */ }
    })();
  }, [API, getHeaders]);

  // ── Load existing voucher when editing ───────────────────────
  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/loyalty/voucher-types?include_inactive=true`,
                                   { headers: getHeaders() });
        const vt = (r.data || []).find(v => v.id === id);
        if (!vt) { toast.error('Voucher not found'); navigate(returnHref); return; }
        setContent({
          outlet_id: vt.outlet_id,
          code: vt.code || '',
          name: vt.name || '',
          description: vt.description || '',
          benefit_type: vt.benefit_type || 'flat_discount',
          benefit_value: vt.benefit_value ?? '',
          validity_days: vt.validity_days ?? 30,
          active: vt.active !== false,
          is_giftable: !!vt.is_giftable,
          sales_enabled: !!vt.sales_enabled,
          terms: vt.terms || '',
          design_template_id: vt.design_template_id || '',
        });
        setDesign(seedEditorDesign(vt));
      } catch (e) {
        toast.error('Failed to load voucher');
      } finally { setLoading(false); }
    })();
  }, [API, getHeaders, id, isNew, navigate]);

  // ── Live voucher context for the canvas ─────────────────────
  // This is what the canonical renderer binds. No sample fallback:
  // every field flows straight from the left panel.
  const liveVoucher = useMemo(() => ({
    name:           content.name || 'New Voucher',
    subtitle:       content.description || '',
    description:    content.description || '',
    code:           content.code || 'PREVIEW',
    voucher_code:   content.code || 'PREVIEW',
    benefit_type:   content.benefit_type,
    benefit_value:  content.benefit_value === '' ? null : Number(content.benefit_value || 0),
    valid_until:    shortDate(content.validity_days),
  }), [content]);

  const outletCtx = useMemo(() => {
    const o = outlets.find(x => x.id === content.outlet_id);
    const name = o?.name || 'Your Brand';
    return {
      name,
      tagline: o?.tagline || o?.subtitle || '',
      logo:    o?.logo_url || o?.brand_logo || '',
      initials: (name || 'BR').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase(),
    };
  }, [outlets, content.outlet_id]);

  // ── Save actions ─────────────────────────────────────────────
  const saveVoucher = async () => {
    if (!content.outlet_id) { toast.error('Select an outlet'); return; }
    if (!/^[A-Z0-9_\-]{2,32}$/.test((content.code || '').toUpperCase())) {
      toast.error('Voucher code must be 2–32 chars: A-Z 0-9 _ -'); return;
    }
    if (!content.name.trim()) { toast.error('Voucher name is required'); return; }
    setSaving(true);
    try {
      // Persist the design INLINE alongside the voucher (V2 shape). If
      // a design_template_id is set, the backend's expander will still
      // resolve the template on read; the inline design is a fallback.
      const payload = {
        ...(isNew ? { outlet_id: content.outlet_id } : {}),
        code:           content.code.toUpperCase(),
        name:           content.name,
        description:    content.description,
        benefit_type:   content.benefit_type,
        benefit_value:  content.benefit_value === '' ? null : Number(content.benefit_value),
        validity_days:  Number(content.validity_days || 30),
        active:         !!content.active,
        is_giftable:    !!content.is_giftable,
        sales_enabled:  !!content.sales_enabled,
        design:         { design_version: 2, canvas: design.canvas, elements: design.elements },
        design_template_id: content.design_template_id || '',
      };
      let res;
      if (isNew) {
        res = await axios.post(`${API}/admin/loyalty/voucher-types`, payload,
                                { headers: getHeaders() });
      } else {
        res = await axios.patch(`${API}/admin/loyalty/voucher-types/${id}`, payload,
                                 { headers: getHeaders() });
      }
      toast.success('Voucher saved');
      if (isNew && res.data?.id) {
        // Retain outlet + origin context on the resulting Builder URL
        // so the ← Voucher Types breadcrumb still returns exactly to
        // the caller's tab + outlet.
        const nsp = new URLSearchParams();
        if (outletFromQuery) nsp.set('outlet_id', outletFromQuery);
        if (originFrom)      nsp.set('from', originFrom);
        navigate(`/admin/loyalty/voucher-builder/${res.data.id}${nsp.toString() ? `?${nsp.toString()}` : ''}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) { toast.error('Template name required'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/loyalty/design-templates`, {
        name: templateName.trim(),
        status: 'draft',
        canvas: design.canvas || {},
        layers: design.elements || [],
      }, { headers: getHeaders() });
      toast.success('Design saved as template');
      setSaveAsTplOpen(false);
      setTemplateName('');
      // Preload for the apply picker.
      setTemplates(t => [r.data, ...t]);
      // Auto-bind the new template to this voucher.
      setContent(c => ({ ...c, design_template_id: r.data.id }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Template save failed');
    } finally { setSaving(false); }
  };

  const applyTemplate = async (tplId) => {
    try {
      const r = await axios.get(`${API}/admin/loyalty/design-templates/${tplId}`,
                                 { headers: getHeaders() });
      // Apply canvas + layers WITHOUT touching content.
      setDesign({ design_version: 2,
                  canvas: r.data.canvas || {},
                  elements: r.data.layers || [] });
      setContent(c => ({ ...c, design_template_id: tplId }));
      setApplyTplOpen(false);
      toast.success(`Applied "${r.data.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-slate-500">Loading voucher…</div>
    );
  }

  return (
    <div className="max-w-[1700px] mx-auto p-3 space-y-3" data-testid="voucher-builder">
      {/* ── Top bar ─────────────────────────────────────────────*/}
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="ghost" onClick={() => navigate(returnHref)}
                 data-testid="builder-back-btn"
                 title="Back to Voucher Types">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Voucher Types
        </Button>
        <div className="flex-1 min-w-[220px]">
          <div className="text-lg font-semibold flex items-center gap-2">
            <PenTool className="w-4 h-4" />
            {isNew ? 'New Voucher' : `Edit · ${content.name || content.code}`}
          </div>
          <div className="text-[11px] text-slate-500">
            One place · content + design + preview · saves as one voucher.
          </div>
        </div>
        <div className="flex items-center gap-1 border border-slate-200 rounded-md p-0.5">
          <button onClick={() => setPreviewMode('desktop')}
                   data-testid="builder-preview-desktop"
                   className={`px-2 py-1 rounded text-xs flex items-center gap-1
                               ${previewMode === 'desktop' ? 'bg-slate-100' : ''}`}>
            <Monitor className="w-3.5 h-3.5" /> Desktop
          </button>
          <button onClick={() => setPreviewMode('mobile')}
                   data-testid="builder-preview-mobile"
                   className={`px-2 py-1 rounded text-xs flex items-center gap-1
                               ${previewMode === 'mobile' ? 'bg-slate-100' : ''}`}>
            <Smartphone className="w-3.5 h-3.5" /> Mobile Wallet
          </button>
        </div>
        <Button size="sm" variant="outline" onClick={() => setApplyTplOpen(true)}
                 data-testid="builder-apply-template">
          <LibraryBig className="w-3.5 h-3.5 mr-1" /> Apply Template
        </Button>
        <Button size="sm" variant="outline" onClick={() => setSaveAsTplOpen(true)}
                 data-testid="builder-save-as-template">
          <BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Save Design as Template
        </Button>
        {content.design_template_id && (
          <Button size="sm" variant="outline"
                   onClick={() => setBulkApplyOpen(true)}
                   data-testid="builder-bulk-apply"
                   title="Apply this template to multiple voucher types">
            <LibraryBig className="w-3.5 h-3.5 mr-1" /> Apply to Voucher Types
          </Button>
        )}
        <Button size="sm" onClick={saveVoucher} disabled={saving}
                 data-testid="builder-save-voucher"
                 className="bg-amber-500 hover:bg-amber-600 text-white">
          <Save className="w-3.5 h-3.5 mr-1" />
          {saving ? 'Saving…' : (isNew ? 'Save Voucher' : 'Update Voucher')}
        </Button>
      </div>

      {/* ── 3-panel layout ─────────────────────────────────────*/}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-3">
        {/* LEFT — voucher content */}
        <ContentPanel
          content={content} setContent={setContent}
          outlets={outlets} isNew={isNew}
        />
        {/* CENTER + RIGHT — canvas editor OR mobile-wallet preview */}
        {previewMode === 'mobile' ? (
          <MobileWalletPreview
            design={design}
            voucher={liveVoucher}
            outlet={outletCtx}
          />
        ) : (
          <div data-testid="builder-canvas-wrap">
            <VoucherDesignEditor
              initialDesign={design}
              voucher={liveVoucher}
              outlet={outletCtx}
              saving={saving}
              onChange={setDesign}
              onSave={(nextDesign) => { setDesign(nextDesign); toast.success('Design applied'); }}
              onClose={() => {}}
            />
          </div>
        )}
      </div>

      {/* ── Save-as-template dialog ───────────────────────────*/}
      <Dialog open={saveAsTplOpen} onOpenChange={setSaveAsTplOpen}>
        <DialogContent className="max-w-md" data-testid="save-as-template-dialog">
          <DialogHeader>
            <DialogTitle>Save Design as Master Template</DialogTitle>
            <DialogDescription>
              Saves only the visual composition (canvas + layers). Any
              voucher can later apply this design; each voucher keeps its
              own business content.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Template name</Label>
            <Input value={templateName} onChange={e => setTemplateName(e.target.value)}
                    placeholder="e.g. Monsoon Premium"
                    data-testid="save-as-template-name" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveAsTplOpen(false)}>Cancel</Button>
            <Button onClick={saveAsTemplate} disabled={saving}
                     data-testid="save-as-template-confirm">
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Apply-template picker ─────────────────────────────*/}
      <Dialog open={applyTplOpen} onOpenChange={setApplyTplOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto"
                        data-testid="apply-template-dialog">
          <DialogHeader>
            <DialogTitle>Apply Existing Template</DialogTitle>
            <DialogDescription>
              Applies the chosen master design to this voucher. Content
              (name, value, code, validity, terms) stays unchanged.
            </DialogDescription>
          </DialogHeader>
          {templates.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">
              No saved templates yet. Design a voucher and click
              &ldquo;Save Design as Template&rdquo; to create one.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {templates.map(t => (
                <button key={t.id} onClick={() => applyTemplate(t.id)}
                         data-testid={`pick-template-${t.id}`}
                         className="text-left rounded-md overflow-hidden ring-1 ring-slate-200 hover:ring-amber-400 transition">
                  <div className="aspect-[1.586] bg-slate-100 flex items-center justify-center">
                    <VoucherDesignRenderer
                      design={{ design_version: 2, canvas: t.canvas || {}, elements: t.layers || [] }}
                      voucher={liveVoucher}
                      outlet={outletCtx}
                      qrValue={liveVoucher.code}
                      validUntil={liveVoucher.valid_until}
                      mode="read"
                      size="phone"
                      className="!max-w-none w-full"
                    />
                  </div>
                  <div className="p-2">
                    <div className="text-sm font-medium truncate">{t.name}</div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Badge className="text-[10px]">{t.status}</Badge>
                      <span className="text-[10px] text-slate-500">{(t.layers?.length || t.layer_count || 0)} layers</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Phase 3B · Slice 4 · Bulk Apply Template to Voucher Types ─*/}
      <BulkApplyTemplateDialog
        open={bulkApplyOpen}
        onOpenChange={setBulkApplyOpen}
        templateId={content.design_template_id}
        templates={templates}
        API={API}
        getHeaders={getHeaders}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//                    Mobile-Wallet Preview
// ══════════════════════════════════════════════════════════════
// Renders the LIVE unsaved draft through the exact same
// VoucherPresentation → VoucherDesignRenderer tree that the Guest
// Wallet uses. Wrapped in a phone-shaped chrome so the admin can see
// real text wrapping, real proportions and real wallet spacing before
// clicking Save. No fake mockup renderer.
function MobileWalletPreview({ design, voucher, outlet }) {
  return (
    <div className="flex items-center justify-center py-6"
         data-testid="builder-mobile-preview">
      <div className="relative"
           style={{ width: 360, borderRadius: 40,
                    background: '#0F172A',
                    padding: 12,
                    boxShadow: '0 40px 90px -30px rgba(0,0,0,0.55), inset 0 0 0 2px rgba(255,255,255,0.06)' }}>
        {/* Notch */}
        <div style={{ position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)',
                       width: 100, height: 22, background: '#000', borderRadius: 12, opacity: 0.9 }} />
        <div style={{ background: '#0B1220', borderRadius: 28, padding: '36px 12px 20px 12px',
                       minHeight: 620 }}>
          <div className="text-center text-[10px] text-white/50 uppercase tracking-wider mb-3">
            Guest Wallet Preview
          </div>
          <div className="flex justify-center">
            {/* Same golden stage the real Guest Wallet uses. */}
            <div className="relative rounded-[26px] p-[6px] w-full max-w-[320px]"
                 style={{
                   background: 'linear-gradient(135deg, rgba(201,162,39,0.30) 0%, rgba(201,162,39,0.06) 40%, rgba(201,162,39,0.30) 100%)',
                   boxShadow: '0 24px 60px -18px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05)',
                 }}>
              <VoucherPresentation
                voucher={voucher}
                design={design}
                outletName={outlet?.name}
                outletTagline={outlet?.tagline}
                outletInitials={outlet?.initials}
                outletLogo={outlet?.logo}
                state="available"
                validUntil={voucher?.valid_until}
                size="phone"
              />
            </div>
          </div>
          <div className="mt-5 text-[10px] text-white/40 text-center">
            Container adapts to the saved aspect ratio.
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//                        Content panel
// ══════════════════════════════════════════════════════════════
function ContentPanel({ content, setContent, outlets, isNew }) {
  const bind = (k) => ({
    value: content[k] ?? '',
    onChange: (e) => setContent(c => ({ ...c, [k]: e.target.value })),
  });
  return (
    <Card className="p-3 space-y-3 h-fit lg:sticky lg:top-3"
          data-testid="content-panel">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">
        Voucher Content
      </div>
      <div>
        <Label className="text-[11px] text-slate-500">Outlet</Label>
        <Select value={content.outlet_id} disabled={!isNew}
                 onValueChange={v => setContent(c => ({ ...c, outlet_id: v }))}>
          <SelectTrigger className="h-9 text-sm" data-testid="content-outlet"><SelectValue placeholder="Choose an outlet" /></SelectTrigger>
          <SelectContent>
            {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-slate-500">Code</Label>
          <Input className="h-9 text-sm uppercase"
                  {...bind('code')}
                  onChange={(e) => setContent(c => ({ ...c, code: e.target.value.toUpperCase() }))}
                  placeholder="MONSOON50"
                  data-testid="content-code" />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Validity (days)</Label>
          <Input type="number" min={1} max={3650} className="h-9 text-sm"
                  value={content.validity_days}
                  onChange={(e) => setContent(c => ({ ...c, validity_days: e.target.value }))}
                  data-testid="content-validity" />
        </div>
      </div>
      <div>
        <Label className="text-[11px] text-slate-500">Name</Label>
        <Input className="h-9 text-sm" {...bind('name')}
                placeholder="e.g. Monsoon Festival"
                data-testid="content-name" />
      </div>
      <div>
        <Label className="text-[11px] text-slate-500">Subtitle / Description</Label>
        <Textarea rows={2} className="text-sm" {...bind('description')}
                   placeholder="Enjoy 50% Discount this season"
                   data-testid="content-subtitle" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] text-slate-500">Benefit Type</Label>
          <Select value={content.benefit_type}
                   onValueChange={v => setContent(c => ({ ...c, benefit_type: v }))}>
            <SelectTrigger className="h-9 text-sm" data-testid="content-benefit-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BENEFIT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Value</Label>
          <Input className="h-9 text-sm" {...bind('benefit_value')}
                  placeholder="50 · 200 · ..."
                  data-testid="content-benefit-value" />
        </div>
      </div>
      <div>
        <Label className="text-[11px] text-slate-500">Terms & Conditions</Label>
        <Textarea rows={3} className="text-sm" {...bind('terms')}
                   placeholder="Minimum bill ₹2,000. Dine-in only."
                   data-testid="content-terms" />
        <div className="text-[10px] text-slate-400 mt-0.5">
          Terms appear behind the &ldquo;Terms &amp; Conditions&rdquo; CTA on the card.
        </div>
      </div>
      <div className="flex items-center justify-between border-t pt-2">
        <div>
          <div className="text-[12px] font-medium">Active</div>
          <div className="text-[10px] text-slate-500">Guests can see and use this voucher</div>
        </div>
        <Switch checked={!!content.active}
                 onCheckedChange={v => setContent(c => ({ ...c, active: v }))}
                 data-testid="content-active" />
      </div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[12px] font-medium">Giftable</div>
          <div className="text-[10px] text-slate-500">Guests can send this voucher as a gift</div>
        </div>
        <Switch checked={!!content.is_giftable}
                 onCheckedChange={v => setContent(c => ({ ...c, is_giftable: v }))}
                 data-testid="content-giftable" />
      </div>
      <div className="flex items-center justify-between border-t pt-2">
        <div>
          <div className="text-[12px] font-medium">Available to Sales Team</div>
          <div className="text-[10px] text-slate-500">
            When enabled, this voucher can be sold by authorised Loyalty Sales
            users at eligible outlets.
          </div>
        </div>
        <Switch checked={!!content.sales_enabled}
                 onCheckedChange={v => setContent(c => ({ ...c, sales_enabled: v }))}
                 data-testid="content-sales-enabled" />
      </div>
      {content.design_template_id && (
        <div className="border-t pt-2 text-[11px] text-slate-500 flex items-center gap-1"
             data-testid="content-bound-template">
          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
          Design template applied ·
          <button className="text-amber-600 hover:underline"
                   onClick={() => setContent(c => ({ ...c, design_template_id: '' }))}>
            detach
          </button>
        </div>
      )}
    </Card>
  );
}


// ══════════════════════════════════════════════════════════════
//    Phase 3B · Slice 4 · Bulk Apply Template to Voucher Types
// ══════════════════════════════════════════════════════════════
//
// Server contract:
//   POST /api/admin/loyalty/design-templates/{template_id}/apply-to-voucher-types
//   Payload:
//     scope: 'selected' | 'all_in_scope'
//     voucher_type_ids: [str]   (required when scope='selected')
//     outlet_ids:       [str]   (optional)
//     dry_run:          bool
//     confirm_count:    int     (required on commit; must equal server count)
//
// UI flow:
//   Step 1 — Scope selection (Selected voucher types | All-in-scope)
//   Step 2 — Dry-run preview (server-resolved count + target list)
//   Step 3 — Explicit confirmation with commercial-safety copy
//   Step 4 — Success (or 409 → back to Step 2 with a warning)
//
// The commercial fields (name / price / benefit / validity / terms /
// sales_enabled / outlet_id) are NEVER shown as editable — the copy in
// Step 3 is explicit.

function BulkApplyTemplateDialog({ open, onOpenChange, templateId, templates, API, getHeaders }) {
  const [step, setStep]                     = useState('scope');   // scope | dryrun | success
  const [scope, setScope]                   = useState('selected');
  const [outlets, setOutlets]               = useState([]);
  const [outletFilter, setOutletFilter]     = useState([]);        // array of outlet ids
  const [voucherTypes, setVoucherTypes]     = useState([]);
  const [selectedVtIds, setSelectedVtIds]   = useState(new Set());
  const [search, setSearch]                 = useState('');
  const [dryRunResult, setDryRunResult]     = useState(null);
  const [applyResult, setApplyResult]       = useState(null);
  const [acknowledged, setAcknowledged]     = useState(false);
  const [busy, setBusy]                     = useState(false);
  const [staleCountWarning, setStaleWarning] = useState(false);

  const tpl = useMemo(
    () => templates.find(t => t.id === templateId) || null,
    [templates, templateId]
  );

  // Reset when opening/closing.
  useEffect(() => {
    if (open) {
      setStep('scope');
      setScope('selected');
      setSelectedVtIds(new Set());
      setSearch('');
      setDryRunResult(null);
      setApplyResult(null);
      setAcknowledged(false);
      setOutletFilter([]);
      setStaleWarning(false);
    }
  }, [open]);

  // Load outlets + voucher types once (all in tenant, incl. inactive
  // — admin design admin may cover drafts).
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [ou, vt] = await Promise.all([
          axios.get(`${API}/outlets`, { headers: getHeaders() }),
          axios.get(`${API}/admin/loyalty/voucher-types?include_inactive=true`,
                    { headers: getHeaders() }),
        ]);
        setOutlets(Array.isArray(ou.data) ? ou.data : []);
        setVoucherTypes(Array.isArray(vt.data) ? vt.data : []);
      } catch (e) {
        toast.error('Failed to load voucher types');
      }
    })();
  }, [open, API, getHeaders]);

  const outletMap = useMemo(() => {
    const m = {}; outlets.forEach((o) => { m[o.id] = o.name; }); return m;
  }, [outlets]);

  const filteredVts = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    return voucherTypes.filter((v) => {
      if (outletFilter.length && !outletFilter.includes(v.outlet_id)) return false;
      if (!q) return true;
      return (v.name || '').toLowerCase().includes(q)
        || (v.code || '').toLowerCase().includes(q);
    });
  }, [voucherTypes, search, outletFilter]);

  const toggleVt = (id) => {
    setSelectedVtIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const selectAllFiltered = () => {
    setSelectedVtIds((prev) => {
      const n = new Set(prev);
      filteredVts.forEach((v) => n.add(v.id));
      return n;
    });
  };
  const clearAll = () => setSelectedVtIds(new Set());

  const runDryRun = async () => {
    if (!templateId) { toast.error('No template selected'); return; }
    if (scope === 'selected' && selectedVtIds.size === 0) {
      toast.error('Select at least one voucher type'); return;
    }
    setBusy(true); setStaleWarning(false);
    try {
      const r = await axios.post(
        `${API}/admin/loyalty/design-templates/${templateId}/apply-to-voucher-types`,
        {
          scope,
          voucher_type_ids: scope === 'selected' ? Array.from(selectedVtIds) : [],
          outlet_ids: outletFilter,
          dry_run: true,
        },
        { headers: getHeaders() },
      );
      setDryRunResult(r.data);
      setStep('dryrun');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Dry-run failed');
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!dryRunResult) return;
    if (!acknowledged) {
      toast.error('Please acknowledge the change'); return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/loyalty/design-templates/${templateId}/apply-to-voucher-types`,
        {
          scope,
          voucher_type_ids: scope === 'selected' ? Array.from(selectedVtIds) : [],
          outlet_ids: outletFilter,
          dry_run: false,
          confirm_count: dryRunResult.resolved_count,
        },
        { headers: getHeaders() },
      );
      setApplyResult(r.data);
      setStep('success');
    } catch (e) {
      // 409 → target set changed between preview and commit. Refresh
      // the dry-run and warn — do NOT retry automatically.
      if (e?.response?.status === 409) {
        setStaleWarning(true);
        setAcknowledged(false);
        toast.error('The voucher selection changed. Please review the updated list before applying.');
        await runDryRun();
        return;
      }
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Apply failed');
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
                      data-testid="bulk-apply-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Apply Template to Voucher Types
          </DialogTitle>
          <DialogDescription>
            Copy this template&apos;s design onto multiple voucher types.
            Commercial fields (name, price, benefit, validity, terms,
            sales availability) will not change.
          </DialogDescription>
        </DialogHeader>

        {tpl && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 flex items-center gap-3">
            <div className="w-16 aspect-[1.586] bg-white rounded shadow-inner overflow-hidden">
              <VoucherDesignRenderer
                design={{ design_version: 2, canvas: tpl.canvas || {}, elements: tpl.layers || [] }}
                voucher={{ name: tpl.name, code: 'PREVIEW' }}
                outlet={{ name: 'Preview', initials: 'PV' }}
                mode="read" size="phone"
                className="!max-w-none w-full"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{tpl.name}</div>
              <div className="text-[11px] text-slate-500">
                Master template · {(tpl.layers?.length || 0)} layers ·
                Status <Badge className="text-[10px] ml-1">{tpl.status}</Badge>
              </div>
            </div>
          </div>
        )}

        {step === 'scope' && (
          <div className="space-y-4 mt-2" data-testid="bulk-apply-step-scope">
            <div className="space-y-2">
              <Label>Scope</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition
                                    ${scope === 'selected' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" checked={scope === 'selected'}
                         onChange={() => setScope('selected')}
                         data-testid="bulk-apply-scope-selected" />
                  <div>
                    <div className="text-sm font-medium">Selected Voucher Types</div>
                    <div className="text-[11px] text-slate-500">Pick exactly which vouchers to update.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition
                                    ${scope === 'all_in_scope' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" checked={scope === 'all_in_scope'}
                         onChange={() => setScope('all_in_scope')}
                         data-testid="bulk-apply-scope-all" />
                  <div>
                    <div className="text-sm font-medium">All Voucher Types in Scope</div>
                    <div className="text-[11px] text-slate-500">Every voucher type in the tenant (optional outlet filter).</div>
                  </div>
                </label>
              </div>
            </div>

            {scope === 'all_in_scope' && (
              <div className="space-y-2">
                <Label>Outlet filter (optional)</Label>
                <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto p-2 rounded border border-slate-200">
                  {outlets.map((o) => (
                    <label key={o.id} className="flex items-center gap-2 text-xs">
                      <Checkbox
                        checked={outletFilter.includes(o.id)}
                        onCheckedChange={() => setOutletFilter((p) =>
                          p.includes(o.id) ? p.filter(x => x !== o.id) : [...p, o.id])}
                        data-testid={`bulk-apply-outlet-${o.id}`}
                      />
                      <span className="truncate">{o.name}</span>
                    </label>
                  ))}
                </div>
                <div className="text-[11px] text-slate-500">
                  Leave empty to include every outlet in your tenant.
                </div>
              </div>
            )}

            {scope === 'selected' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Voucher types</Label>
                  <div className="flex items-center gap-2">
                    <button type="button" className="text-xs text-amber-600 hover:underline"
                            onClick={selectAllFiltered}
                            data-testid="bulk-apply-select-all">
                      Select all filtered
                    </button>
                    <span className="text-slate-300">·</span>
                    <button type="button" className="text-xs text-slate-500 hover:underline"
                            onClick={clearAll}
                            data-testid="bulk-apply-clear-all">
                      Clear
                    </button>
                  </div>
                </div>
                <Input placeholder="Search by name or code"
                       value={search} onChange={(e) => setSearch(e.target.value)}
                       data-testid="bulk-apply-search" />
                <div className="border border-slate-200 rounded max-h-64 overflow-y-auto divide-y">
                  {filteredVts.length === 0 && (
                    <div className="p-4 text-center text-xs text-slate-400">No voucher types match.</div>
                  )}
                  {filteredVts.map((v) => (
                    <label key={v.id}
                           className={`flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer
                                       ${selectedVtIds.has(v.id) ? 'bg-amber-50' : ''}`}
                           data-testid={`bulk-apply-vt-row-${v.id}`}>
                      <Checkbox
                        checked={selectedVtIds.has(v.id)}
                        onCheckedChange={() => toggleVt(v.id)}
                        data-testid={`bulk-apply-vt-cb-${v.id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{v.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono">
                          {v.code} · {outletMap[v.outlet_id] || v.outlet_id}
                        </div>
                      </div>
                      {!v.active && <span className="text-[10px] text-slate-400">inactive</span>}
                    </label>
                  ))}
                </div>
                <div className="text-[11px] text-slate-500">
                  {selectedVtIds.size} selected
                </div>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={runDryRun} disabled={busy}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="bulk-apply-preview-btn">
                {busy ? 'Loading…' : 'Preview Changes'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'dryrun' && dryRunResult && (
          <div className="space-y-4 mt-2" data-testid="bulk-apply-step-dryrun">
            {staleCountWarning && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800">
                  The voucher selection changed between preview and commit.
                  We&apos;ve refreshed the list — please review before applying.
                </div>
              </div>
            )}

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">
                    Will update
                  </div>
                  <div className="text-3xl font-bold text-slate-900"
                       data-testid="bulk-apply-count">
                    {dryRunResult.resolved_count}
                  </div>
                  <div className="text-xs text-slate-500">Voucher Types</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-slate-500 uppercase tracking-widest">Template</div>
                  <div className="text-sm font-semibold">{dryRunResult.template?.name}</div>
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-slate-700 mb-2">Targets</div>
              <div className="border border-slate-200 rounded max-h-56 overflow-y-auto divide-y"
                   data-testid="bulk-apply-target-list">
                {(dryRunResult.targets || []).map((t) => (
                  <div key={t.id} className="p-2 text-xs flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{t.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        {t.code} · {outletMap[t.outlet_id] || t.outlet_id}
                      </div>
                    </div>
                    {!t.active && <span className="text-[10px] text-slate-400">inactive</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-sm font-semibold text-emerald-900 mb-1">
                This changes DESIGN ONLY.
              </div>
              <div className="text-xs text-emerald-800">
                Voucher names, selling prices, benefits, validity, terms
                and sales availability will not change. Applying a template
                copies its current design — future edits to the template
                will not automatically change these vouchers.
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer"
                   data-testid="bulk-apply-acknowledge-row">
              <Checkbox checked={acknowledged}
                        onCheckedChange={setAcknowledged}
                        data-testid="bulk-apply-acknowledge" />
              <span>
                I understand that the design of{' '}
                <strong>{dryRunResult.resolved_count} voucher type
                {dryRunResult.resolved_count === 1 ? '' : 's'}</strong> will be
                replaced.
              </span>
            </label>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep('scope')}>Back</Button>
              <Button onClick={commit}
                      disabled={busy || !acknowledged || dryRunResult.resolved_count === 0}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="bulk-apply-commit-btn">
                {busy ? 'Applying…' : `Apply Design to ${dryRunResult.resolved_count} Voucher${dryRunResult.resolved_count === 1 ? '' : 's'}`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'success' && applyResult && (
          <div className="text-center py-6" data-testid="bulk-apply-step-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <div className="text-lg font-bold text-slate-900 mb-1">Design Applied</div>
            <div className="text-sm text-slate-700">
              <div className="font-semibold">{applyResult.template?.name}</div>
              <div className="mt-1">
                <span className="text-2xl font-bold text-slate-900"
                      data-testid="bulk-apply-success-count">
                  {applyResult.applied}
                </span>
                <span className="text-slate-500 text-xs ml-1">
                  Voucher Type{applyResult.applied === 1 ? '' : 's'} updated
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Commercial product data unchanged.
              </div>
            </div>
            <div className="mt-4">
              <Button onClick={() => onOpenChange(false)}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="bulk-apply-done-btn">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
